/**
 * LIVEINFRA query/mutation hooks (LIVEINFRA.md §2 routes, DECISIONS #48).
 *
 * Polling is attention-bounded on the client too: the overlay refetches at
 * the 5s band cadence ONLY while (a) some component is observing the query
 * — i.e. a live-capable view is actually on screen — and (b) the overlay
 * says a source is connected. No source (or no server support yet) → the
 * one probe result is cached and no interval runs; connecting invalidates
 * and polling starts on its own.
 *
 * LIVE mode (#48f — the flow Play toggle): while `liveModeByRepo` is set
 * for the repo, the SAME query polls at 2s with `?mode=live` — the explicit
 * signal the server's redis cadence keys on. One query key either way: the
 * whole map shares one cache entry, so switching modes never forks state.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiDelete, apiGet, apiPost, ApiClientError } from '../../api/client';
import { queryKeys } from '../../api/queries';
import type { RepoSummary, RuntimeMap, Ulid } from '../../api/types';
import { useLiveViewStore } from './envStore';
import { CLIENT_POLL_MS, hasConnectedSource, LIVE_MODE_POLL_MS } from './liveModel';
import type { ConnectSourceRequest, InfraSource, LiveOverlay, LiveSourceKind } from './types';

/**
 * The live overlay for a repo. `null` (not an error) when the server has no
 * live routes yet or no overlay for this repo — every consumer treats null
 * as "nothing connected" and renders today's static UI pixel-identical.
 */
export function useLiveOverlay(rid: Ulid | undefined): UseQueryResult<LiveOverlay | null> {
  const liveMode = useLiveViewStore((s) => (rid !== undefined ? s.liveModeByRepo[rid] === true : false));
  return useQuery({
    queryKey: queryKeys.infraLive(rid ?? ''),
    queryFn: async ({ signal }): Promise<LiveOverlay | null> => {
      try {
        const mode = useLiveViewStore.getState().liveModeByRepo[rid ?? ''] === true;
        return await apiGet<LiveOverlay>(
          `/repos/${rid}/infra/live${mode ? '?mode=live' : ''}`,
          signal,
        );
      } catch (err) {
        // 404: route not shipped / repo unknown to the live layer; 501:
        // capability absent. Both mean "no live data", never a red screen.
        if (err instanceof ApiClientError && (err.status === 404 || err.status === 501)) {
          return null;
        }
        throw err;
      }
    },
    enabled: rid !== undefined && rid.length > 0,
    retry: false,
    staleTime: (liveMode ? LIVE_MODE_POLL_MS : CLIENT_POLL_MS) - 1_000,
    gcTime: 5 * 60_000,
    refetchInterval: (query) =>
      hasConnectedSource(query.state.data ?? null)
        ? liveMode
          ? LIVE_MODE_POLL_MS
          : CLIENT_POLL_MS
        : false,
  });
}

/** POST /api/repos/{rid}/infra/sources — probe-then-save (§9.2; #48 for redis). */
/**
 * The runtime map (DECISIONS #86): manifest truth per environment with the
 * live sample folded in, the drawing, the findings, the history. A look is
 * the attention signal, so this polls only while something renders it,
 * and only briskly when a source is connected.
 */
/** The code dirs and service names a question touched (DECISIONS #88). */
export interface RuntimeMapScope {
  dirs: readonly string[];
  services: readonly string[];
  /** the files the answer cited: each kept part's code facts narrow to them */
  files?: readonly string[];
  /** the runtime entity ids the board's parts named: the cut is exactly these */
  entities?: readonly string[];
}

function scopeQuery(scope: RuntimeMapScope | undefined): string {
  const entities = scope?.entities ?? [];
  if (scope === undefined || (scope.dirs.length === 0 && scope.services.length === 0 && entities.length === 0)) return '';
  const q = new URLSearchParams();
  if (entities.length > 0) q.set('entities', entities.join(','));
  if (scope.dirs.length > 0) q.set('dirs', scope.dirs.join(','));
  if (scope.services.length > 0) q.set('services', scope.services.join(','));
  if (scope.files !== undefined && scope.files.length > 0) q.set('files', scope.files.join(','));
  return `?${q.toString()}`;
}

export function useRuntimeMap(
  rid: Ulid | undefined,
  scope?: RuntimeMapScope,
  opts: { poll?: boolean } = {},
): UseQueryResult<RuntimeMap> {
  const query = scopeQuery(scope);
  return useQuery({
    queryKey: [...queryKeys.infraMap(rid ?? ''), query],
    queryFn: ({ signal }) => apiGet<RuntimeMap>(`/repos/${rid}/infra/map${query}`, signal),
    enabled: rid !== undefined && rid.length > 0,
    retry: false,
    staleTime: 15_000,
    // A look is the server's attention signal: only a view that shows the
    // map keeps looking (DECISIONS #93); a hidden tab asks once.
    refetchInterval: (query) =>
      opts.poll !== false && query.state.data?.live.connected === true ? 30_000 : false,
  });
}

/**
 * The first codebase of a product whose map has a runtime (a deploy
 * manifest with something to draw), or null. One look per codebase, no
 * polling: the pane only needs to know whether there is a tab to offer.
 */
export function useRuntimeRepo(repos: readonly RepoSummary[]): RepoSummary | null {
  const results = useQueries({
    queries: repos.map((repo) => ({
      queryKey: [...queryKeys.infraMap(repo.id), ''],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        apiGet<RuntimeMap>(`/repos/${repo.id}/infra/map`, signal),
      retry: false,
      staleTime: 15_000,
    })),
  });
  const at = results.findIndex((r) => r.data?.has_runtime === true);
  return at === -1 ? null : (repos[at] ?? null);
}

export function useConnectInfraSource(rid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectSourceRequest) =>
      apiPost<InfraSource>(`/repos/${rid}/infra/sources`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.infraLive(rid) });
      // The §9 panel's second card (live_sources + connection state) rides
      // the provenance payload.
      void qc.invalidateQueries({ queryKey: queryKeys.provenance(rid) });
      // The map folds the live sample in: it must not keep the pre-connect picture.
      void qc.invalidateQueries({ queryKey: queryKeys.infraMap(rid) });
    },
  });
}

/**
 * DELETE /api/repos/{rid}/infra/sources/{kind}[?environment=] — disconnect
 * deletes the source row(s) and Secrets slot(s) immediately (gating law 5).
 * Redis rows are per (repo, env) — DECISIONS #48a: `environment` scopes the
 * disconnect to one env; omitted, every redis env dies at once.
 */
export function useDisconnectInfraSource(rid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: LiveSourceKind | { kind: LiveSourceKind; environment?: string }) => {
      const { kind, environment } = typeof arg === 'string' ? { kind: arg, environment: undefined } : arg;
      const suffix =
        environment !== undefined && environment.length > 0
          ? `?environment=${encodeURIComponent(environment)}`
          : '';
      return apiDelete(`/repos/${rid}/infra/sources/${kind}${suffix}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.infraLive(rid) });
      void qc.invalidateQueries({ queryKey: queryKeys.provenance(rid) });
      // The map folds the live sample in: it must not keep the pre-connect picture.
      void qc.invalidateQueries({ queryKey: queryKeys.infraMap(rid) });
    },
  });
}
